import { getApp, getApps, initializeApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyBgzxsjTAssD_0xF1m0RbqkRZVrb1vLygg',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'sandd-66863.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'sandd-66863',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'sandd-66863.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '1019899241045',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:1019899241045:android:c4e40b5428fbf8c8982adc',
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

export function formatFirebaseError(error) {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please login instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/operation-not-allowed':
      return 'Sign-in method is not enabled. In Firebase Console, go to Authentication > Sign-in method and click Save on Google and Email/Password.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized in Firebase Console. Go to Authentication > Settings > Authorized Domains and verify localhost.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few moments and try again.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow popups or use redirect.';
    default:
      return error?.message || 'Authentication error. Please try again.';
  }
}

export const ADMIN_EMAIL = 'saipannaikwadi8177@gmail.com';

export function isUserAdmin(email) {
  return String(email || '').trim().toLowerCase() === ADMIN_EMAIL;
}

export function formatUser(firebaseUser, profileData = {}) {
  if (!firebaseUser) return null;
  const email = (firebaseUser.email || '').trim().toLowerCase();
  const isAdmin = email === ADMIN_EMAIL;

  return {
    id: firebaseUser.uid,
    uid: firebaseUser.uid,
    email,
    name: profileData.name || firebaseUser.displayName || (email ? email.split('@')[0] : 'User'),
    role: isAdmin ? 'admin' : 'user',
    isVerified: Boolean(firebaseUser.emailVerified),
    avatarUrl: profileData.avatarUrl || firebaseUser.photoURL || '',
  };
}

export async function signupWithFirebase({ name, email, password }) {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    const user = cred.user;

    if (name && name.trim()) {
      try {
        await updateProfile(user, { displayName: name.trim() });
      } catch (_) {}
    }

    try {
      sendEmailVerification(user).catch(() => {});
    } catch (_) {}

    const profile = {
      uid: user.uid,
      name: (name || '').trim() || user.displayName || '',
      email: (user.email || '').trim().toLowerCase(),
      role: 'user',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Non-blocking firestore write (never hangs if database is not created yet)
    setDoc(doc(db, 'users', user.uid), profile, { merge: true }).catch(() => {});

    return {
      user: formatUser(user, profile),
      message: 'Signup successful! Welcome.',
    };
  } catch (err) {
    throw new Error(formatFirebaseError(err));
  }
}

export async function loginWithFirebase({ email, password }) {
  try {
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    const user = cred.user;

    return {
      user: formatUser(user),
      message: 'Login successful.',
    };
  } catch (err) {
    throw new Error(formatFirebaseError(err));
  }
}

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  let user = null;
  if (Capacitor.isNativePlatform()) {
    const result = await FirebaseAuthentication.signInWithGoogle();
    const idToken = result.credential?.idToken;
    if (!idToken) {
      throw new Error('Google sign-in did not return an ID token. Check the Android Firebase configuration.');
    }
    const credential = GoogleAuthProvider.credential(idToken, result.credential?.accessToken);
    const firebaseResult = await signInWithCredential(auth, credential);
    user = firebaseResult.user;
  } else {
    try {
      const cred = await signInWithPopup(auth, provider);
      user = cred.user;
    } catch (popupErr) {
      console.warn('Popup error, attempting redirect fallback:', popupErr?.code || popupErr?.message);
      if (
        popupErr?.code === 'auth/popup-blocked' ||
        popupErr?.code === 'auth/popup-closed-by-user' ||
        popupErr?.code === 'auth/cancelled-popup-request' ||
        String(popupErr?.message || '').includes('Cross-Origin-Opener-Policy')
      ) {
        await signInWithRedirect(auth, provider);
        return { redirecting: true };
      }
      throw new Error(formatFirebaseError(popupErr));
    }
  }

  if (!user) {
    throw new Error('Google sign-in was not completed.');
  }

  const profile = {
    uid: user.uid,
    name: user.displayName || (user.email ? user.email.split('@')[0] : 'User'),
    email: (user.email || '').trim().toLowerCase(),
    avatarUrl: user.photoURL || '',
    role: 'user',
    lastLoginAt: serverTimestamp(),
  };

  setDoc(doc(db, 'users', user.uid), profile, { merge: true }).catch(() => {});

  return {
    user: formatUser(user, profile),
    message: 'Signed in with Google successfully.',
  };
}

export async function getRedirectResultUser() {
  try {
    const result = await getRedirectResult(auth);
    if (result?.user) {
      const u = formatUser(result.user);
      const profile = {
        uid: result.user.uid,
        name: result.user.displayName || (result.user.email ? result.user.email.split('@')[0] : 'User'),
        email: (result.user.email || '').trim().toLowerCase(),
        avatarUrl: result.user.photoURL || '',
        role: 'user',
        lastLoginAt: serverTimestamp(),
      };
      setDoc(doc(db, 'users', result.user.uid), profile, { merge: true }).catch(() => {});
      return u;
    }
  } catch (err) {
    console.warn('Redirect result check:', err?.message || err);
  }
  return null;
}

export async function resetPasswordWithFirebase({ email }) {
  try {
    await sendPasswordResetEmail(auth, email.trim());
    return {
      success: true,
      message: 'A password reset link has been sent to your email address.',
    };
  } catch (err) {
    throw new Error(formatFirebaseError(err));
  }
}

export async function logoutWithFirebase() {
  try {
    await signOut(auth);
  } catch (_) {}
}

export async function resendFirebaseVerification() {
  if (auth.currentUser) {
    try {
      await sendEmailVerification(auth.currentUser);
      return { success: true, message: 'Verification email resent.' };
    } catch (err) {
      throw new Error(formatFirebaseError(err));
    }
  }
  throw new Error('No active user to send verification email to.');
}
